import { useState } from "react";
import JSZip from "jszip";
import { supabase } from "@/lib/supabase";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";
import { Logger } from "@/lib/logger/Logger";
import { useStorage, withMimeExtension } from "../../hooks/useStorage";
import { showNotification } from "../../store/Central/selectors";

const FETCH_CONCURRENCY = 5;
/** Ids per id→path resolution request, so a select-all set never overflows the request URL. */
const ID_RESOLVE_CHUNK = 100;

export interface ZipProgress {
	phase: "fetch" | "pack";
	done: number;
	total: number;
	percent: number;
}

/** Runs `worker` over `items` at most `limit` at a time, preserving order. */
async function pool<T, R>(
	items: T[],
	limit: number,
	worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
	const results: R[] = new Array(items.length);
	let cursor = 0;
	const runners = Array.from(
		{ length: Math.min(limit, items.length) },
		async () => {
			while (cursor < items.length) {
				const index = cursor++;
				results[index] = await worker(items[index], index);
			}
		}
	);
	await Promise.all(runners);
	return results;
}

/**
 * Builds a ZIP from a set of archive file ids. Resolves the ids to storage paths itself (so it
 * works for a select-all set that was never rendered), fetches with bounded concurrency, and
 * reports real progress for both the fetch and the pack phase.
 */
export const useArchiveZip = () => {
	const { fetchFileFromPath, downloadBlob } = useStorage();
	const [isZipping, setIsZipping] = useState(false);
	const [progress, setProgress] = useState<ZipProgress | null>(null);

	const downloadZip = async (fileIds: string[], zipName: string) => {
		if (fileIds.length === 0 || isZipping) return;

		setIsZipping(true);
		setProgress({
			phase: "fetch",
			done: 0,
			total: fileIds.length,
			percent: 0,
		});

		try {
			// Resolve ids in chunks: a select-all set can hold up to SELECT_ALL_CAP ids, and a
			// single .in() would serialise them into a GET query string long enough to trip
			// gateway URL-length limits (414/400).
			const rows: {
				id: string;
				file_name: string | null;
				path_name: string;
			}[] = [];
			for (let i = 0; i < fileIds.length; i += ID_RESOLVE_CHUNK) {
				const chunk = fileIds.slice(i, i + ID_RESOLVE_CHUNK);
				const { data, error } = await supabase
					.from(SupabaseTableEnum.FILES)
					.select("id, file_name, path_name")
					.in("id", chunk);
				if (error || !data) throw error ?? new Error("no rows");
				rows.push(...data);
			}

			const zip = new JSZip();
			const usedNames = new Set<string>();
			let done = 0;
			// Files whose row was not returned (no longer visible) count as failures too.
			let failed = fileIds.length - rows.length;

			await pool(rows, FETCH_CONCURRENCY, async (row) => {
				try {
					if (!row.path_name) {
						failed++;
						return;
					}
					const { success, data: signedUrl } =
						await fetchFileFromPath(row.path_name);
					if (!success || !signedUrl) {
						failed++;
						return;
					}
					const response = await fetch(signedUrl);
					if (!response.ok) {
						failed++;
						return;
					}
					const blob = await response.blob();
					let name = withMimeExtension(
						row.file_name || "Unbenannte_Datei",
						blob.type
					);
					if (usedNames.has(name)) {
						const dot = name.lastIndexOf(".");
						const base = dot > 0 ? name.slice(0, dot) : name;
						const ext = dot > 0 ? name.slice(dot) : "";
						name = `${base}_${row.id.slice(0, 8)}${ext}`;
					}
					usedNames.add(name);
					zip.file(name, blob);
				} catch (fileError) {
					failed++;
					Logger.error(fileError, {}, "Error fetching file for zip");
				} finally {
					done++;
					setProgress({
						phase: "fetch",
						done,
						total: rows.length,
						percent: Math.round((done / rows.length) * 100),
					});
				}
			});

			if (usedNames.size === 0) {
				showNotification({
					message: "Keine Datei konnte geladen werden",
					type: "error",
				});
				return;
			}

			const blob = await zip.generateAsync({ type: "blob" }, (meta) => {
				setProgress({
					phase: "pack",
					done: usedNames.size,
					total: usedNames.size,
					percent: Math.round(meta.percent),
				});
			});
			await downloadBlob(blob, zipName, true);

			if (failed > 0) {
				showNotification({
					message: `${failed} Datei(en) konnten nicht geladen werden`,
					type: "warning",
				});
			}
		} catch (error) {
			Logger.error(error, {}, "Error building archive zip");
			showNotification({
				message: "Fehler beim Erstellen des ZIP-Archivs",
				type: "error",
			});
		} finally {
			setIsZipping(false);
			setProgress(null);
		}
	};

	return { isZipping, progress, downloadZip };
};
